import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SkipSelf,
  TemplateRef
} from '@angular/core';
import { NgbDropdownAnchor, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { of, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FireflyChipModule } from '../../../chips/chip.module';
import { FireflyEditableChipGroupComponent } from '../../../chips/editable-chip-group/editable-chip-group.component';
import { ChipType } from '../../../chips/models/chip-type';
import { FireflyModalService, ModalRef } from '../../../modal';
import { FireflyPopoverModule } from '../../../popovers/popover.module';
import { FIREFLY_SUGGESTER } from '../../../suggesters/constants';
import { Breakpoint, FireflyLocalizationService, FireflyNumberFormattingService } from '../../../utils';
import { FireflyFormattingModule } from '../../../utils/formatting/formatting.module';
import { FireflyLocationCollapsedPopoverComponent } from '../location-collapsed-popover/location-collapsed-popover.component';

/**
 * `f-location-editable-chip-group`
 *
 * Extends `f-editable-chip-group` with location-specific additions WITHOUT
 * modifying the base component:
 *
 * 1. `customPopoverTpl` Input — passed to both the desktop popover (via template)
 *    and the mobile modal (via context → `f-location-collapsed-popover`).
 * 2. `modalOpened` / `modalClosed` Outputs — emitted when the mobile chip-group
 *    modal opens/closes so that `f-location-chip-group` can manage the
 *    pending-items buffer.
 * 3. Popover-jump fix — when chips expand while the popover is open, defer the
 *    state update until Popper.js has finished its async cleanup, preventing the
 *    overlay from snapping to the top-left corner of the viewport.
 * 4. Opens `f-location-collapsed-popover` (instead of `f-collapsed-popover`)
 *    on mobile so the custom template is rendered inside the modal.
 */
@Component({
  selector: 'f-location-editable-chip-group',
  standalone: true,
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['customPopoverTpl', 'type'],
  // eslint-disable-next-line @angular-eslint/no-outputs-metadata-property
  outputs: ['modalOpened', 'modalClosed'],
  imports: [
    CommonModule,
    FireflyChipModule,
    FireflyPopoverModule,
    FireflyFormattingModule,
    FireflyLocationCollapsedPopoverComponent
  ],
  templateUrl: './location-editable-chip-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyLocationEditableChipGroupComponent
  extends FireflyEditableChipGroupComponent
  implements OnChanges, OnDestroy
{
  /** Custom popover/modal template — shown instead of the default concised-item list. */
  @Input() customPopoverTpl: TemplateRef<unknown> | null = null;

  /** Default chip type for the location suggester. */
  @Input() override type: ChipType = ChipType.EditableSecondary;

  /** Emits when the mobile chip-group modal opens. */
  @Output() readonly modalOpened = new EventEmitter<void>();

  /** Emits `true` on confirm, `false` on dismiss when the mobile modal closes. */
  @Output() readonly modalClosed = new EventEmitter<boolean>();

  // ─── Local references to injected services (parent keeps them private) ───
  private readonly _cdr: ChangeDetectorRef;
  private readonly _modalSvc: FireflyModalService;

  /** Separate destroyed$ for this subclass's subscriptions. */
  private readonly _locationDestroyed$ = new Subject<void>();

  constructor(
    zone: NgZone,
    el: ElementRef,
    cdr: ChangeDetectorRef,
    modalService: FireflyModalService,
    @Optional() @SkipSelf() dropdownAnchor: NgbDropdownAnchor,
    @Optional() localizationService: FireflyLocalizationService,
    @Optional() numberFormattingService: FireflyNumberFormattingService,
    @Optional() @Inject(FIREFLY_SUGGESTER) suggester: unknown
  ) {
    super(zone, el, cdr, modalService, dropdownAnchor, localizationService, numberFormattingService, suggester);
    this._cdr = cdr;
    this._modalSvc = modalService;
  }

  override isCollapse(items: Record<string, unknown>[]): boolean {
    const wrapper = this.el.nativeElement.parentElement as HTMLElement | null; // f-location-chip-group
    const container = wrapper?.parentElement as HTMLElement | null; // flex anchor div

    if (wrapper && container) {
      const containerStyles = window.getComputedStyle(container);
      const containerWidth =
        container.clientWidth -
        parseInt(containerStyles.paddingLeft || '0', 10) -
        parseInt(containerStyles.paddingRight || '0', 10);

      if (containerWidth > 0) {
        // Grant the wrapper a concrete width for the measurement only.
        wrapper.style.setProperty('width', `${containerWidth}px`);
        wrapper.style.setProperty('display', 'block');
        const result = super.isCollapse(items);
        // Immediately restore — no visual side-effect.
        wrapper.style.removeProperty('width');
        wrapper.style.removeProperty('display');
        return result;
      }
    }

    return super.isCollapse(items);
  }

  // ─── Popover-jump fix ─────────────────────────────────────────────────────
  /**
   * Override ngOnChanges to add the popover-jump fix.
   * When chips are about to expand AND the desktop popover is open, Popper.js
   * resets the overlay position asynchronously after `popover.close()`. If we
   * expand (hide the collapsed chip) immediately, Popper positions itself
   * against a hidden anchor and jumps to top-left corner.
   * Fix: listen to the popover's `hidden` event and only expand AFTER the
   * overlay has been fully torn down.
   *
   * To avoid calling `isCollapse()` twice (it has side-effects), we only
   * intercept when the popover is actually open — otherwise super handles
   * everything as-is.
   */
  override ngOnChanges(): void {
    if (
      this.popover?.isOpen() &&
      this.items &&
      this.collapsedChipContainer &&
      (!this.collapsable || !this.isCollapse(this.items))
    ) {
      // Defer expanded-state update until Popper.js cleanup is complete.
      this.popover.hidden
        .pipe(take(1), takeUntil(this._locationDestroyed$))
        .subscribe(() => this._applyLocationExpandedState());
      this.popover.close();
      return;
    }
    super.ngOnChanges();
  }

  override ngOnDestroy(): void {
    this._locationDestroyed$.next();
    this._locationDestroyed$.complete();
    super.ngOnDestroy();
  }

  // ─── Mobile modal override ────────────────────────────────────────────────
  /**
   * Override to open `f-location-collapsed-popover` (which supports
   * `customPopoverTpl`) instead of the base `f-collapsed-popover`,
   * and to emit the `modalOpened` / `modalClosed` events needed by
   * `f-location-chip-group`'s pending-items buffer.
   */
  override onCollapsedChipClick($event: Event, popover: NgbPopover): void {
    if (this.disabled && !this.isPartOfSuggester) return;

    if (window.innerWidth >= Breakpoint.Sm) {
      popover.toggle();
      return;
    }

    // Snapshot current items for potential reset on dismiss.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).initialItems = this.items?.length ? [...this.items] : [];

    const modalDialogClass = `mobile-collapsed-popover ${
      this.disabled && this.isPartOfSuggester ? 'disabled-suggester-popover' : ''
    }`;

    // Open the location-specific collapsed-popover (supports customPopoverTpl).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).modal = this._modalSvc.open({
      component: FireflyLocationCollapsedPopoverComponent,
      title: this.collapsedChipGroupModalTitle,
      modalDialogClass,
      context: this, // passes customPopoverTpl to FireflyLocationCollapsedPopoverComponent
      mobile: true
    });

    this.modalOpened.emit();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((this as any).modal as ModalRef<unknown>).result
      .then(
        () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this as any).handleStateUpdate();
          this.modalClosed.emit(true);
        },
        () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this as any).resetState();
          this.modalClosed.emit(false);
        }
      )
      .finally(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any).modal = null;
      });
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private _applyLocationExpandedState(): void {
    this.collapsed = false;
    this.visibleChips = this.items;
    this.hasChipsCssClass = !!this.items?.length;
    this.collapsedText$ = of('');
    this._cdr.detectChanges();
  }
}
